<template>
  <v-card color="transparent" elevation="0">
    <v-row v-if="!finishedLoading" class="d-flex justify-center flex-grow py-16">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </v-row>

    <v-row v-else>
      <v-col cols="12" class="d-flex justify-end pb-0 align-center flex-wrap">
        <v-btn class="mr-2 mb-2" @click="showRecurringManager = true">
          <v-icon left>mdi-sync</v-icon>
          Daueraufträge
        </v-btn>
        <v-btn class="mr-2 mb-2" color="primary" @click="showExpenseDialog = true">
          <v-icon left>mdi-plus-box</v-icon>
          Ausgabe erfassen
        </v-btn>

        <v-btn-toggle v-model="selectedDataType" mandatory dense dark class="mb-2">
          <v-btn value="invoices">Debitoren</v-btn>
          <v-btn value="payments">Zahlungen</v-btn>
        </v-btn-toggle>

        <div class="ml-4 d-flex align-center mb-2" v-if="selectedDataType === 'payments'">
          <v-switch v-model="deductVAT" inset dense hide-details color="green accent-4" class="mt-0 pt-0">
            <template v-slot:label>
              <span class="white--text caption font-weight-bold">
                {{ deductVAT ? "Netto (exkl. 8.1%)" : "Brutto" }}
              </span>
            </template>
          </v-switch>
        </div>
      </v-col>

      <template v-if="data && data.totalRevenue > 0">
        <v-col cols="12" sm="6" md="4" lg="2">
          <v-card class="pa-3 fill-height rounded-lg kpi-card" @click="openYoyDialog">
            <div class="d-flex align-center mb-1">
              <v-icon large color="green accent-4">mdi-cash-multiple</v-icon>
              <div class="ml-4 flex-grow-1">
                <div class="d-flex justify-space-between align-center">
                  <div class="caption grey--text">Gesamtumsatz</div>
                  <v-chip v-if="deductVAT && selectedDataType === 'payments'" x-small color="green accent-4" outlined label class="px-1">
                    Netto
                  </v-chip>
                </div>
                <div class="h4 font-weight-bold white--text">
                  <number :from="0" :to="getEffectiveValue(data.totalRevenue)" :format="formatCurrency"></number>
                </div>
                <div
                  v-if="deductVAT && selectedDataType === 'payments'"
                  class="caption red--text text--accent-2 mt-1"
                  style="line-height: 1.2; font-size: 0.7rem">
                  davon MwSt: -{{ formatCurrency(getVatAmount(data.totalRevenue)) }}
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4" lg="2">
          <v-card class="pa-3 fill-height rounded-lg kpi-card" hover @click="showInvoiceDialog = true">
            <div class="d-flex align-center">
              <v-icon large color="red accent-3">mdi-file-chart-outline</v-icon>
              <div class="ml-4">
                <div class="caption grey--text">Ausgaben (Rechn.)</div>
                <div class="h4 font-weight-bold white--text">
                  <number :from="0" :to="invoiceTotal" :format="formatCurrency"></number>
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4" lg="2">
          <v-card class="pa-3 fill-height rounded-lg kpi-card">
            <div class="d-flex align-center">
              <v-icon large color="light-green accent-3">mdi-scale-balance</v-icon>
              <div class="ml-4">
                <div class="caption grey--text">Geschätzter Gewinn</div>
                <div class="h4 font-weight-bold white--text">
                  <number :from="0" :to="estimatedProfit" :format="formatCurrency"></number>
                </div>
                <div v-if="deductVAT && selectedDataType === 'payments'" class="caption grey--text mt-1" style="font-size: 0.7rem">
                  (Netto-Umsatz - Ausgaben)
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4" lg="2">
          <v-card class="pa-3 fill-height rounded-lg kpi-card">
            <div class="d-flex align-center">
              <v-icon large color="blue lighten-1">mdi-receipt-text-check-outline</v-icon>
              <div class="ml-4">
                <div class="caption grey--text">Transaktionen</div>
                <div class="h4 font-weight-bold white--text">
                  <number :from="0" :to="data.totalTransactions"></number>
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4" lg="2">
          <v-card class="pa-3 fill-height rounded-lg kpi-card">
            <div class="d-flex align-center">
              <v-icon
                large
                :color="
                  comparisonToPreviousYear.trend === 'up' ? 'green accent-4' : comparisonToPreviousYear.trend === 'down' ? 'red accent-3' : 'grey'
                ">
                {{
                  comparisonToPreviousYear.trend === "up"
                    ? "mdi-trending-up"
                    : comparisonToPreviousYear.trend === "down"
                    ? "mdi-trending-down"
                    : "mdi-trending-neutral"
                }}
              </v-icon>
              <div class="ml-4">
                <div class="caption grey--text">Vergleich Vorjahr</div>
                <div v-if="comparisonToPreviousYear.percentage !== null" class="h4 font-weight-bold white--text">
                  <span v-if="comparisonToPreviousYear.percentage === Infinity"> +∞ % </span>
                  <span v-else> {{ comparisonToPreviousYear.percentage > 0 ? "+" : "" }}{{ comparisonToPreviousYear.percentage.toFixed(1) }} % </span>
                </div>
                <div v-else class="h4 font-weight-bold grey--text">N/A</div>
              </div>
            </div>
            <div v-if="data.previousYearTotalRevenue > 0" class="caption grey--text text-right mt-2">
              VJ: {{ formatCurrency(getEffectiveValue(data.previousYearTotalRevenue)) }}
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4" lg="2">
          <v-card class="pa-3 fill-height rounded-lg kpi-card">
            <div class="d-flex align-center">
              <v-icon large color="teal lighten-1">mdi-account-group</v-icon>
              <div class="ml-4">
                <div class="caption grey--text">Einzigartige Kunden</div>
                <div class="h4 font-weight-bold white--text">
                  <number :from="0" :to="data.uniqueCustomerCount || 0"></number>
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="12" md="4" lg="2" @click="showDetailsDialog = true">
          <v-card class="pa-3 fill-height rounded-lg kpi-card" hover>
            <div class="d-flex align-center">
              <v-icon large color="purple lighten-2">mdi-file-document-outline</v-icon>
              <div class="ml-4">
                <div class="caption grey--text">Details</div>
                <div class="h4 font-weight-bold white--text">Alle Anzeigen...</div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="4" md="4" lg="2">
          <v-card class="pa-3 fill-height rounded-lg kpi-card">
            <div class="d-flex align-center mb-1">
              <v-icon large color="deep-purple lighten-1">mdi-file-sign</v-icon>
              <div class="ml-4">
                <div class="caption grey--text">Umsatz Abos</div>
                <div class="h4 font-weight-bold white--text d-flex align-baseline">
                  <number :to="getEffectiveValue(revenueByCategoryType.subscriptions)" :format="formatCurrency"></number>
                  <span class="caption ml-2 grey--text">({{ revenuePercentages.subscriptions }}%)</span>
                </div>
                <div
                  v-if="deductVAT && selectedDataType === 'payments'"
                  class="caption red--text text--accent-2 mt-1"
                  style="line-height: 1.2; font-size: 0.7rem">
                  MwSt: -{{ formatCurrency(getVatAmount(revenueByCategoryType.subscriptions)) }}
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="4" md="4" lg="2">
          <v-card class="pa-3 fill-height rounded-lg kpi-card">
            <div class="d-flex align-center mb-1">
              <v-icon large color="orange darken-1">mdi-package-variant-closed</v-icon>
              <div class="ml-4">
                <div class="caption grey--text">Umsatz Pakete</div>
                <div class="h4 font-weight-bold white--text d-flex align-baseline">
                  <number :to="getEffectiveValue(revenueByCategoryType.packages)" :format="formatCurrency"></number>
                  <span class="caption ml-2 grey--text">({{ revenuePercentages.packages }}%)</span>
                </div>
                <div
                  v-if="deductVAT && selectedDataType === 'payments'"
                  class="caption red--text text--accent-2 mt-1"
                  style="line-height: 1.2; font-size: 0.7rem">
                  MwSt: -{{ formatCurrency(getVatAmount(revenueByCategoryType.packages)) }}
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="4" md="4" lg="2">
          <v-card class="pa-3 fill-height rounded-lg kpi-card">
            <div class="d-flex align-center mb-1">
              <v-icon large color="grey">mdi-shape-outline</v-icon>
              <div class="ml-4">
                <div class="caption grey--text">Umsatz Sonstiges</div>
                <div class="h4 font-weight-bold white--text d-flex align-baseline">
                  <number :to="getEffectiveValue(revenueByCategoryType.other)" :format="formatCurrency"></number>
                  <span class="caption ml-2 grey--text">({{ revenuePercentages.other }}%)</span>
                </div>
                <div
                  v-if="deductVAT && selectedDataType === 'payments'"
                  class="caption red--text text--accent-2 mt-1"
                  style="line-height: 1.2; font-size: 0.7rem">
                  MwSt: -{{ formatCurrency(getVatAmount(revenueByCategoryType.other)) }}
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4" lg="2">
          <v-card class="pa-3 fill-height rounded-lg kpi-card">
            <div class="d-flex align-center">
              <v-icon large color="light-blue accent-3">mdi-account-cash</v-icon>
              <div class="ml-4">
                <div class="caption grey--text">Ø Umsatz / Kunde</div>
                <div class="h4 font-weight-bold white--text">
                  <number :to="getEffectiveValue(avgRevenuePerCustomer)" :format="formatCurrency"></number>
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" class="mt-4">
          <v-card class="pa-4 rounded-lg" color="#1F2937">
            <apexchart height="350" type="area" :series="revenueOverTimeSeries" :options="revenueOverTimeOptions"></apexchart>
          </v-card>
        </v-col>

        <v-col cols="12" class="mt-4">
          <v-card class="pa-4 rounded-lg" color="#1F2937">
            <div class="d-flex justify-space-between align-center mb-4 flex-wrap">
              <v-card-title class="white--text pa-0">{{ chartTitle }}</v-card-title>
              <div class="d-flex align-center">
                <v-btn v-if="drilldownLevel > 0" @click="drillUp" small icon color="primary" class="mr-4">
                  <v-icon>mdi-arrow-up</v-icon>
                </v-btn>
                <v-select
                  :items="mainCategorySelectItems"
                  item-text="name"
                  item-value="name"
                  v-model="selectedMainCategory"
                  label="Hauptkategorie wählen"
                  dense
                  outlined
                  dark
                  hide-details
                  style="max-width: 250px"></v-select>
              </div>
            </div>
            <apexchart
              v-if="dynamicChartSeries.length > 0"
              height="400"
              type="bar"
              :series="[{ name: 'Umsatz', data: dynamicChartSeries }]"
              :options="dynamicChartOptions"
              @dataPointSelection="handleChartClick"></apexchart>
            <div v-else class="text-center grey--text py-16">Für diese Kategorie gibt es keine Daten im ausgewählten Zeitraum.</div>
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card class="pa-4 rounded-lg fill-height" color="#1F2937">
            <apexchart height="350" type="donut" :series="mainCategorySeries.series" :options="mainCategoryOptions"></apexchart>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card class="pa-4 rounded-lg fill-height" color="#1F2937">
            <apexchart height="350" type="pie" :series="genderSeries.series" :options="genderOptions"></apexchart>
          </v-card>
        </v-col>
      </template>

      <template v-else>
        <v-col cols="12" class="text-center py-16">
          <h2 class="white--text font-weight-light">
            Für den ausgewählten Zeitraum wurden keine Daten für "{{ selectedDataType === "invoices" ? "Rechnungen" : "Zahlungen" }}" gefunden.
          </h2>
        </v-col>
      </template>
    </v-row>

    <v-dialog v-model="showRecurringManager" fullscreen hide-overlay transition="dialog-bottom-transition">
      <RecurringExpenseManager
        v-if="showRecurringManager"
        :studios="studios"
        :invoice-issuers="invoiceIssuers"
        @close="showRecurringManager = false" />
    </v-dialog>

    <v-row no-gutters>
      <v-col cols="12" class="mt-4">
        <v-card class="pa-2 rounded-lg" color="#1F2937">
          <v-card-title class="white--text pa-0 mb-4">Analyse der Promotionen & Gutschriften</v-card-title>

          <v-data-table
            :headers="[
              { text: 'Promotion / Gutschrift', value: 'name', width: '60%' },
              { text: 'Anzahl Nutzungen', value: 'count', align: 'end' },
              { text: 'Gesamtwert', value: 'totalValue', align: 'end' },
            ]"
            :items="adjustmentsSummary"
            :loading="adjustmentsLoading"
            no-data-text="Keine Promotionsdaten im ausgewählten Zeitraum gefunden."
            dark
            class="elevation-0 transparent"
            hide-default-footer
            disable-pagination>
            <template v-slot:item.totalValue="{ item }">
              <span class="font-weight-bold">{{ formatCurrency(item.totalValue) }}</span>
            </template>

            <template v-slot:body.append>
              <tr class="font-weight-bold" style="background-color: rgba(59, 130, 246, 0.1)">
                <td class="text-right white--text title">Gesamt:</td>
                <td class="text-right white--text title pr-4">{{ adjustmentsTotal.count }}</td>
                <td class="text-right white--text title pr-4">{{ formatCurrency(adjustmentsTotal.totalValue) }}</td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="false">
      <v-col cols="12" md="6">
        <v-card class="pa-4 rounded-lg" color="#1F2937">
          <v-card-title class="white--text pa-0 mb-4">Top 5 nach Umsatz</v-card-title>
          <v-list dense color="transparent" class="pa-0">
            <v-list-item v-for="(item, i) in top5ByRevenue" :key="`rev-${i}`" class="white--text">
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action class="font-weight-bold">
                {{ formatCurrency(getEffectiveValue(item.total)) }}
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card class="pa-4 rounded-lg" color="#1F2937">
          <v-card-title class="white--text pa-0 mb-4">Top 5 nach Verkäufen</v-card-title>
          <v-list dense color="transparent" class="pa-0">
            <v-list-item v-for="(item, i) in top5ByCount" :key="`count-${i}`" class="white--text">
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action class="font-weight-bold"> {{ item.count }} Stk. </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showDetailsDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card color="#111827">
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="showDetailsDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          <v-toolbar-title>Detailansicht der {{ selectedDataType === "invoices" ? "Rechnungen" : "Zahlungen" }}</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-card color="#1F2937" class="pa-4 mb-4 rounded-lg">
            <v-row dense>
              <v-col cols="12" md="3"
                ><v-text-field v-model="filters.customer" label="Nach Kunde filtern..." dark dense outlined clearable hide-details></v-text-field
              ></v-col>
              <v-col cols="12" md="3"
                ><v-autocomplete
                  v-model="filters.contractName"
                  :items="uniqueContractNames"
                  label="Nach Vertragsname filtern..."
                  dark
                  dense
                  outlined
                  clearable
                  hide-details></v-autocomplete
              ></v-col>
              <v-col cols="12" md="3"
                ><v-text-field
                  v-model="filters.description"
                  label="Nach Beschreibung filtern..."
                  dark
                  dense
                  outlined
                  clearable
                  hide-details></v-text-field
              ></v-col>
              <v-col cols="12" md="3"
                ><v-autocomplete
                  v-model="filters.accountName"
                  :items="uniqueAccountNames"
                  label="Nach Konto filtern..."
                  dark
                  dense
                  outlined
                  clearable
                  hide-details></v-autocomplete
              ></v-col>
              <v-col cols="12" md="3" class="d-flex align-center">
                <v-checkbox v-model="filters.hasAdjustment" label="Nur mit Korrektur" dark hide-details class="mt-0 pt-0"></v-checkbox>
              </v-col>
            </v-row>
          </v-card>

          <v-data-table
            :headers="detailsHeaders"
            :items="filteredDetailedTransactions"
            :loading="detailsLoading"
            :server-items-length="totalDetails"
            :options.sync="paginationOptions"
            @update:options="fetchTransactionDetails"
            dark
            class="elevation-2 transparent">
            <template v-slot:item.BILLDATE="{ item }">{{ new Date(item.BILLDATE).toLocaleString("de-CH") }}</template>
            <template v-slot:item.VALUE="{ item }">
              <div v-if="!item.adjustment" class="font-weight-bold">
                {{ formatCurrency(Math.abs(getEffectiveValue(item.VALUE))) }}
              </div>
              <div v-else>
                <del class="grey--text text--lighten-1">{{ formatCurrency(Math.abs(getEffectiveValue(item.VALUE))) }}</del>

                <div class="d-flex justify-end align-center">
                  <v-icon small color="green accent-4" class="mr-1">mdi-plus-circle-outline</v-icon>
                  <span class="green--text text--accent-4">{{ formatCurrency(getEffectiveValue(item.adjustment.value)) }}</span>
                </div>
                <v-divider dark class="my-1"></v-divider>

                <div class="font-weight-bold white--text">= {{ formatCurrency(Math.abs(getEffectiveValue(item.netValue))) }}</div>
              </div>
            </template>
            <template v-slot:item.customer="{ item }">{{ item.FIRSTNAME }} {{ item.LASTNAME }}</template>
            <template v-slot:body.append>
              <tr class="font-weight-bold" style="background-color: rgba(59, 130, 246, 0.1)">
                <td :colspan="detailsHeaders.length - 1" class="text-right white--text title">Total der gefilterten Einträge:</td>
                <td class="text-right white--text title pr-4">{{ formatCurrency(filteredTotalValue) }}</td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showYoyDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card color="#111827">
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="showYoyDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          <v-toolbar-title>Umsatzentwicklung im Jahresvergleich</v-toolbar-title>
        </v-toolbar>

        <v-card-text class="pt-4">
          <v-card color="#1F2937" class="pa-4 mb-4 rounded-lg">
            <v-row align="center">
              <v-col cols="12" md="auto">
                <v-select
                  v-model="yoyYearsBack"
                  :items="[2, 3, 4, 5, 6, 7, 8, 9, 10, 11]"
                  label="Jahre zurück"
                  dark
                  dense
                  outlined
                  hide-details></v-select>
              </v-col>
              <v-col cols="12" md="5">
                <div class="d-flex align-center">
                  <v-autocomplete
                    v-if="yoyData"
                    v-model="yoySelectedStudios"
                    :items="Object.keys(yoyData.results)"
                    label="Studios auswählen"
                    dark
                    dense
                    outlined
                    hide-details
                    multiple
                    chips
                    deletable-chips
                    small-chips>
                    <template v-slot:selection="{ item, index }">
                      <v-chip small v-if="index < 2">
                        <span>{{ item }}</span>
                      </v-chip>
                      <span v-if="index === 2" class="grey--text text-caption ml-1"> (+{{ yoySelectedStudios.length - 2 }} weitere) </span>
                    </template>
                  </v-autocomplete>

                  <v-btn
                    icon
                    :color="areAllStudiosSelected ? 'red' : 'primary'"
                    class="ml-2"
                    @click="toggleSelectAllStudios"
                    :title="areAllStudiosSelected ? 'Auswahl aufheben' : 'Alle Studios auswählen'">
                    <v-icon>{{ areAllStudiosSelected ? "mdi-close-box-multiple" : "mdi-check-all" }}</v-icon>
                  </v-btn>
                </div>
              </v-col>

              <template v-if="yoyViewMode === 'custom'">
                <v-col cols="12" md="auto">
                  <v-select
                    v-model="compareYearA"
                    :items="availableYearsForComparison"
                    label="Jahr A"
                    dark
                    dense
                    outlined
                    hide-details
                    style="min-width: 150px"></v-select>
                </v-col>
                <v-col cols="12" md="auto">
                  <v-select
                    v-model="compareYearB"
                    :items="availableYearsForComparison.filter((y) => y !== compareYearA)"
                    label="Jahr B"
                    dark
                    dense
                    outlined
                    hide-details
                    style="min-width: 150px"></v-select>
                </v-col>
              </template>
              <v-col cols="12" md="auto" class="d-flex justify-end">
                <v-btn-toggle v-model="yoyViewMode" mandatory dense dark background-color="#111827">
                  <v-btn value="combined">
                    <v-icon left>mdi-chart-bar</v-icon>
                    Gesamt
                  </v-btn>
                  <v-btn value="split">
                    <v-icon left>mdi-chart-bar-stacked</v-icon>
                    Direktvergleich
                  </v-btn>
                  <v-btn value="custom">
                    <v-icon left>mdi-select-compare</v-icon>
                    Freier Vergleich
                  </v-btn>
                </v-btn-toggle>
              </v-col>
            </v-row>
          </v-card>

          <div v-if="yoyLoading" class="text-center py-16">
            <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          </div>

          <div v-else-if="yoyComparisonData.years && yoyComparisonData.years.length > 0">
            <v-row v-if="yoyViewMode !== 'custom'">
              <v-col cols="12" sm="6" md="3">
                <v-card class="pa-3 rounded-lg kpi-card fill-height">
                  <div class="caption grey--text">YTD Umsatz vs Vorjahr</div>
                  <div class="h5 font-weight-bold white--text">{{ formatCurrency(yoyComparisonData.kpis.ytdCurrent) }}</div>
                  <div
                    class="d-flex align-center"
                    :class="yoyComparisonData.kpis.ytdGrowth >= 0 ? 'green--text text--accent-4' : 'red--text text--accent-3'">
                    <v-icon small class="mr-1">{{ yoyComparisonData.kpis.ytdGrowth >= 0 ? "mdi-arrow-top-right" : "mdi-arrow-bottom-left" }}</v-icon>
                    <span>{{ yoyComparisonData.kpis.ytdGrowth.toFixed(1) }}%</span>
                    <span class="caption grey--text ml-2">vs. {{ formatCurrency(yoyComparisonData.kpis.ytdPrevious) }}</span>
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-card class="pa-3 rounded-lg kpi-card fill-height" hover @click="openRankDialog('revenue')">
                  <div class="caption grey--text">Bester Monat (All-Time)</div>
                  <div class="h5 font-weight-bold white--text">{{ yoyComparisonData.kpis.bestMonth }}</div>
                  <div class="body-2 white--text">{{ formatCurrency(yoyComparisonData.kpis.bestMonthRevenue) }}</div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-card class="pa-3 rounded-lg kpi-card fill-height" hover @click="openRankDialog('growth')">
                  <div class="caption grey--text">Stärkstes Monats-Wachstum</div>
                  <div class="h5 font-weight-bold white--text">{{ yoyComparisonData.kpis.bestGrowth }}</div>
                  <div class="body-2 green--text text--accent-4">+{{ yoyComparisonData.kpis.bestGrowthValue.toFixed(1) }}%</div>
                </v-card>
              </v-col>
            </v-row>

            <v-row v-if="yoyViewMode !== 'custom'">
              <v-col cols="12" lg="6">
                <v-card color="#1F2937" class="pa-4 mt-4 rounded-lg">
                  <v-card-title class="white--text pt-0 pl-0">Kumulierte Umsatzentwicklung</v-card-title>
                  <apexchart height="350" type="line" :series="cumulativeChartSeries" :options="cumulativeChartOptions"></apexchart>
                </v-card>
              </v-col>
              <v-col cols="12" lg="6">
                <v-card color="#1F2937" class="pa-4 mt-4 rounded-lg">
                  <v-card-title class="white--text pt-0 pl-0">Umsatz nach Quartal</v-card-title>
                  <apexchart height="350" type="bar" :series="quarterlyChartSeries" :options="quarterlyChartOptions"> </apexchart>
                </v-card>
              </v-col>
            </v-row>

            <v-divider dark class="my-4"></v-divider>
            <h2 class="white--text font-weight-light mb-4">Monatlicher Umsatzvergleich</h2>

            <div v-if="yoyViewMode === 'combined'">
              <v-card color="#1F2937" class="pa-4 rounded-lg">
                <apexchart height="400" type="line" :series="mainYoYChartSeries" :options="yoyChartOptions"></apexchart>
              </v-card>
            </div>

            <div v-if="yoyViewMode === 'split'">
              <v-row>
                <v-col v-for="(chart, index) in yoySplitChartData" :key="index" cols="12" xl="6">
                  <v-card color="#1F2937" class="pa-4 rounded-lg">
                    <v-card-title class="white--text pt-0 pl-0">{{ chart.title }}</v-card-title>
                    <apexchart height="400" type="bar" :series="chart.series" :options="chart.options"></apexchart>
                  </v-card>
                </v-col>
              </v-row>
            </div>
          </div>
          <div v-if="yoyViewMode === 'custom'">
            <v-card v-if="customComparisonChartData" color="#1F2937" class="pa-4 rounded-lg">
              <apexchart height="400" type="bar" :series="customComparisonChartData.series" :options="customComparisonChartData.options"></apexchart>
            </v-card>
            <div v-else class="text-center grey--text py-16">Bitte wählen Sie zwei Jahre für den Vergleich aus.</div>
          </div>
        </v-card-text>
      </v-card>
      <v-dialog dark v-model="showRankDialog" class="rounded-lg" max-width="600px">
        <v-card color="#111827 rounded-lg">
          <v-toolbar dark color="primary" dense>
            <v-toolbar-title>{{ rankDialogTitle }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="showRankDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-toolbar>
          <v-card-text class="pt-4">
            <v-data-table
              :headers="rankDialogHeaders"
              :items="rankDialogItems"
              dark
              class="elevation-0 transparent"
              hide-default-footer
              disable-pagination
              dense>
              <template v-slot:item.revenue="{ item }">
                <span class="font-weight-bold">{{ formatCurrency(item.revenue) }}</span>
              </template>
              <template v-slot:item.growth="{ item }">
                <span :class="item.growth >= 0 ? 'green--text text--accent-4' : 'red--text text--accent-3'" class="font-weight-bold">
                  {{ item.growth > 0 ? "+" : "" }}{{ item.growth.toFixed(1) }}%
                </span>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-dialog>
    <v-dialog v-model="showInvoiceDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <InvoiceAnalysis @refresh="fetchInvoicesStatistics" v-if="invoiceStats" :stats="invoiceStats" @close="showInvoiceDialog = false" />
    </v-dialog>

    <v-dialog v-model="showExpenseDialog" max-width="700px">
      <ExpenseCaptureForm
        :studios="studios"
        :invoice-issuers="invoiceIssuers"
        @close="showExpenseDialog = false"
        @expense-saved="handleExpenseSaved" />
    </v-dialog>
  </v-card>
</template>

<script>
import api from "../../../api"
import InvoiceAnalysis from "./invoices/InvoiceAnalysis.vue"
import ExpenseCaptureForm from "./invoices/ExpenseCaptureForm.vue"
import RecurringExpenseManager from "./invoices/RecurringExpenseManager.vue" // Pfad anpassen
export default {
  name: "HomeDashboardTransactionStatistics",
  props: {
    datePickerRange: { type: Array, default: () => [] },
    studios: { type: Array, default: () => [] },
  },
  components: {
    InvoiceAnalysis,
    ExpenseCaptureForm,
    RecurringExpenseManager,
  },
  data() {
    return {
      // NEU: Für die MwSt Berechnung
      deductVAT: false,
      vatRate: 0.081, // 8.1%

      compareYearA: null,
      compareYearB: null,
      yoyViewMode: "combined",
      showRankDialog: false,
      rankDialogTitle: "",
      rankDialogHeaders: [],
      rankDialogItems: [],
      showRecurringManager: false,
      invoiceIssuers: [],
      invoiceStats: null,
      showInvoiceDialog: false,
      showExpenseDialog: false,
      showYoyDialog: false,
      yoyLoading: false,
      yoyData: null,
      yoyYearsBack: 3,
      yoySelectedStudios: ["Gesamt"],
      adjustmentsSummary: [],
      adjustmentsLoading: false,
      finishedLoading: false,
      data: null,
      selectedDataType: "invoices",
      showDetailsDialog: false,
      detailsLoading: false,
      detailedTransactions: [],
      totalDetails: 0,
      paginationOptions: {
        page: 1,
        itemsPerPage: 5000,
      },
      detailsHeaders: [
        { text: "Datum", value: "BILLDATE", width: "15%" },
        { text: "Kunde", value: "customer", width: "15%" },
        { text: "Beschreibung", value: "DESCRIPTION", width: "25%" },
        { text: "Vertragsname", value: "ContractName", width: "20%" },
        { text: "Konto", value: "AccountName", width: "10%" },
        { text: "Betrag", value: "VALUE", align: "end", width: "15%" },
      ],
      filters: {
        hasAdjustment: false,
        customer: "",
        contractName: "",
        description: "",
        accountName: "",
      },
      chartColors: ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#6B7280"],
      selectedMainCategory: "Abonnements",
      drilldownLevel: 0,
      drilldownHistory: [],
      drilldownItemData: null,
    }
  },
  async created() {
    this.invoiceIssuers = await api.invoiceIssuers.fetch()

    await this.fetchTransactionStatistics()
    await this.fetchInvoicesStatistics()
  },
  methods: {
    // NEU: Zentrale Methode zur Berechnung des Netto-Werts
    getEffectiveValue(value) {
      if (!value) return 0
      // Wenn Switch AN ist UND wir bei Zahlungen sind, MwSt abziehen
      if (this.deductVAT && this.selectedDataType === "payments") {
        return value / (1 + this.vatRate)
      }
      return value
    },
    // NEU: Hilfsfunktion für die Anzeige der abgezogenen Steuer
    getVatAmount(value) {
      if (!value) return 0
      const net = value / (1 + this.vatRate)
      return value - net
    },

    toggleSelectAllStudios() {
      if (this.areAllStudiosSelected) {
        this.yoySelectedStudios = ["Gesamt"]
      } else {
        this.yoySelectedStudios = this.allStudioNames
      }
    },
    openRankDialog(type) {
      const months = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"]

      const allMonths = this.yoyComparisonData.years.flatMap((year) =>
        year.monthlyDetails.map((month, index) => ({
          name: `${months[index]} ${year.year}`,
          revenue: month.revenue,
          growth: month.growth,
          year: year.year,
        }))
      )

      switch (type) {
        case "revenue":
          this.rankDialogTitle = "Monate sortiert nach Umsatz"
          this.rankDialogHeaders = [
            { text: "Rang", value: "rank", sortable: false },
            { text: "Monat", value: "name", sortable: false },
            { text: "Umsatz", value: "revenue", align: "end", sortable: false },
          ]
          this.rankDialogItems = allMonths.sort((a, b) => b.revenue - a.revenue).map((item, index) => ({ ...item, rank: index + 1 }))
          break

        case "growth":
          this.rankDialogTitle = "Monate sortiert nach Wachstum (YoY)"
          this.rankDialogHeaders = [
            { text: "Rang", value: "rank", sortable: false },
            { text: "Monat", value: "name", sortable: false },
            { text: "YoY Wachstum", value: "growth", align: "end", sortable: false },
          ]
          const oldestYear = [...this.yoyData.years].sort((a, b) => a - b)[0]
          this.rankDialogItems = allMonths
            .filter((m) => m.year > oldestYear)
            .sort((a, b) => b.growth - a.growth)
            .map((item, index) => ({ ...item, rank: index + 1 }))
          break
      }

      this.showRankDialog = true
    },
    async fetchInvoicesStatistics() {
      if (!(this.datePickerRange?.length === 2 && this.studios?.length > 0)) return

      try {
        const params = {
          startDate: this.datePickerRange[0],
          endDate: this.datePickerRange[1],
          studio: this.studios.map((s) => s._id),
        }
        this.invoiceStats = await api.documents.fetchInvoiceStatistics(params)
      } catch (error) {
        console.error("Fehler beim Abrufen der Rechnungs-Statistiken:", error)
        this.$toast.error("Fehler beim Abrufen der Rechnungs-Statistiken")
      }
    },

    handleExpenseSaved() {
      this.showExpenseDialog = false
      this.$toast.success("Ausgabe erfolgreich erfasst!")
      this.fetchInvoicesStatistics()
    },
    openYoyDialog() {
      this.showYoyDialog = true
      this.fetchYoyData()
    },
    async fetchYoyData() {
      this.yoyLoading = true
      try {
        this.yoyData = await api.fetchYoyComparison({
          range: this.datePickerRange,
          studios: this.studios.map((s) => s._id),
          years_back: this.yoyYearsBack,
          dataType: this.selectedDataType,
        })
        if (!this.yoyData.results[this.yoySelectedStudio]) {
          this.yoySelectedStudio = "Gesamt"
        }
      } catch (error) {
        console.error("Fehler beim Laden der YoY-Daten:", error)
      } finally {
        this.yoyLoading = false
      }
    },
    async fetchTransactionStatistics() {
      if (this.datePickerRange?.length === 2 && this.studios?.length > 0) {
        this.finishedLoading = false
        this.adjustmentsLoading = true

        try {
          const payload = {
            range: this.datePickerRange,
            studios: this.studios.map((s) => s._id),
            dataType: this.selectedDataType,
          }
          const [statsData, adjustmentsData] = await Promise.all([api.fetchTransactionStatistics(payload), api.fetchAdjustmentsSummary(payload)])
          this.data = statsData
          this.adjustmentsSummary = adjustmentsData
        } catch (error) {
          console.error("Fehler beim Laden der Statistiken:", error)
          this.data = null
          this.adjustmentsSummary = null
        } finally {
          this.finishedLoading = true
          this.adjustmentsLoading = false
        }
      }
    },
    async fetchTransactionDetails() {
      this.detailsLoading = true
      const response = await api.fetchTransactionDetails({
        range: this.datePickerRange,
        studios: this.studios.map((s) => s._id),
        dataType: this.selectedDataType,
        options: this.paginationOptions,
      })
      this.detailedTransactions = response.items
      this.totalDetails = Number(response.total)
      this.detailsLoading = false
    },
    async fetchDrilldownData(mainCat, subCat) {
      this.drilldownItemData = null
      this.drilldownItemData = await api.fetchTransactionStatisticsDrill({
        range: this.datePickerRange,
        studios: this.studios.map((s) => s._id),
        mainCategory: mainCat,
        subCategory: subCat,
        dataType: this.selectedDataType,
      })
    },
    handleChartClick(event, chartContext, config) {
      if (this.drilldownLevel === 0) {
        const subCategoryName = config.w.globals.labels[config.dataPointIndex]
        this.drilldownHistory = [this.selectedMainCategory, subCategoryName]
        this.drilldownLevel = 1
        this.fetchDrilldownData(this.selectedMainCategory, subCategoryName)
      }
    },
    drillUp() {
      this.drilldownLevel = 0
      this.drilldownHistory = []
      this.drilldownItemData = null
    },
    formatCurrency(value) {
      if (typeof value !== "number") {
        return "0.00 CHF"
      }
      const formattedNumber = value.toLocaleString("de-CH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      return `${formattedNumber} CHF`
    },
  },
  computed: {
    comparisonToPreviousYear() {
      if (!this.data || !this.data.previousYearTotalRevenue) {
        return { percentage: null, trend: "neutral" }
      }
      if (this.data.previousYearTotalRevenue === 0) {
        return { percentage: Infinity, trend: "up" }
      }

      // Wir nehmen hier bewusst die Rohdaten für den %-Vergleich, da der Prozentsatz bei Brutto/Netto identisch ist
      // Aber für die Anzeige müssen wir Effective Value nutzen (wird im Template gemacht)
      const current = this.data.totalRevenue
      const previous = this.data.previousYearTotalRevenue
      const percentage = ((current - previous) / previous) * 100

      let trend = "neutral"
      if (percentage > 0) trend = "up"
      if (percentage < 0) trend = "down"

      return { percentage, trend }
    },
    allStudioNames() {
      if (!this.yoyData) return []
      return Object.keys(this.yoyData.results).filter((name) => name !== "Gesamt")
    },
    areAllStudiosSelected() {
      return this.yoySelectedStudios.length === this.allStudioNames.length
    },
    availableYearsForComparison() {
      if (!this.yoyComparisonData.years) return []
      return this.yoyComparisonData.years.map((y) => y.year).sort((a, b) => b - a)
    },
    aggregatedStudioDataForYoY() {
      if (!this.yoyData || this.yoySelectedStudios.length === 0) {
        return null
      }

      const aggregatedData = {}
      const allYears = this.yoyData.years

      allYears.forEach((year) => {
        aggregatedData[year] = Array(12).fill(0)
      })

      this.yoySelectedStudios.forEach((studioName) => {
        const studioData = this.yoyData.results[studioName]
        if (studioData) {
          for (const year in studioData) {
            if (aggregatedData[year]) {
              for (let i = 0; i < 12; i++) {
                aggregatedData[year][i] += studioData[year][i] || 0
              }
            }
          }
        }
      })

      return aggregatedData
    },
    yoyChartTitle() {
      switch (this.yoyViewMode) {
        case "combined":
          return "Monatlicher Umsatzvergleich (Gesamtübersicht)"
        case "split":
          return "Monatlicher Umsatzvergleich (Jahr vs. Vorjahr)"
        case "custom":
          return `Freier Vergleich: ${this.compareYearA || ""} vs. ${this.compareYearB || ""}`
        default:
          return "Monatlicher Umsatzvergleich"
      }
    },
    customComparisonChartData() {
      if (!this.compareYearA || !this.compareYearB || !this.yoyComparisonData.years) return null

      const yearA_data = this.yoyComparisonData.years.find((y) => y.year === this.compareYearA)
      const yearB_data = this.yoyComparisonData.years.find((y) => y.year === this.compareYearB)

      if (!yearA_data || !yearB_data) return null

      return {
        series: [
          { name: yearA_data.year.toString(), data: yearA_data.monthlyDetails.map((m) => m.revenue) },
          { name: yearB_data.year.toString(), data: yearB_data.monthlyDetails.map((m) => m.revenue) },
        ],
        options: {
          ...this.yoyChartOptions,
          dataLabels: { enabled: false },
          tooltip: {
            theme: "dark",
            shared: true,
            intersect: false,
            y: {
              formatter: (val, { seriesIndex, dataPointIndex, w }) => {
                const seriesName = w.globals.seriesNames[seriesIndex]
                const valueA = yearA_data.monthlyDetails[dataPointIndex].revenue
                const valueB = yearB_data.monthlyDetails[dataPointIndex].revenue

                if (valueA > 0) {
                  const growth = ((valueB - valueA) / valueA) * 100
                  const sign = growth > 0 ? "+" : ""
                  const color = growth > 0 ? "#10B981" : "#EF4444"

                  if (seriesName == yearB_data.year.toString()) {
                    return `${this.formatCurrency(val)} <span style="font-weight: 600; color: ${color};">(${sign}${growth.toFixed(1)}% vs ${
                      yearA_data.year
                    })</span>`
                  }
                }
                return this.formatCurrency(val)
              },
            },
          },
        },
      }
    },
    invoiceTotal() {
      return this.invoiceStats?.grandTotal?.totalAmount || 0
    },
    estimatedProfit() {
      // NEU: Hier ziehen wir die MwSt vom Umsatz ab, bevor wir die Kosten abziehen
      const revenue = this.getEffectiveValue(this.data?.totalRevenue || 0)
      return revenue - this.invoiceTotal
    },
    adjustmentsTotal() {
      if (!this.adjustmentsSummary) return { totalValue: 0, count: 0 }
      return this.adjustmentsSummary.reduce(
        (acc, item) => {
          acc.totalValue += item.totalValue
          acc.count += item.count
          return acc
        },
        { totalValue: 0, count: 0 }
      )
    },
    allItems() {
      if (!this.data) return []
      const subscriptions = this.data.revenueBySubscription || []
      const others = this.data.revenueByCategory || []
      const subItems = subscriptions.flatMap((s) => s.items || [])
      const otherItems = others.flatMap((c) => c.items || [])

      const combined = [...subItems, ...otherItems].reduce((acc, item) => {
        if (!acc[item.name]) {
          acc[item.name] = { name: item.name, total: 0, count: 0 }
        }
        acc[item.name].total += item.total
        acc[item.name].count += item.count
        return acc
      }, {})

      return Object.values(combined)
    },
    top5ByRevenue() {
      return this.allItems.sort((a, b) => b.total - a.total).slice(0, 5)
    },
    top5ByCount() {
      return this.allItems.sort((a, b) => b.count - a.count).slice(0, 5)
    },
    avgRevenuePerCustomer() {
      if (!this.data || !this.data.uniqueCustomerCount) {
        return 0
      }
      return this.data.totalRevenue / this.data.uniqueCustomerCount
    },
    revenueByCategoryType() {
      if (!this.data || !this.data.totalRevenue) {
        return { subscriptions: 0, packages: 0, other: 0 }
      }

      const subscriptions = this.data.revenueBySubscription?.reduce((sum, item) => sum + item.total, 0) || 0
      const packages = this.data.revenueByCategory?.filter((cat) => cat.main === "Pakete").reduce((sum, item) => sum + item.total, 0) || 0
      const other = this.data.totalRevenue - subscriptions - packages

      return { subscriptions, packages, other }
    },
    revenuePercentages() {
      if (!this.data || !this.data.totalRevenue) {
        return { subscriptions: 0, packages: 0, other: 0 }
      }
      const total = this.data.totalRevenue
      // Für die Prozentzahlen nutzen wir die Brutto-Werte, da das Verhältnis gleich bleibt
      const revenues = this.revenueByCategoryType
      return {
        subscriptions: ((revenues.subscriptions / total) * 100).toFixed(1),
        packages: ((revenues.packages / total) * 100).toFixed(1),
        other: ((revenues.other / total) * 100).toFixed(1),
      }
    },
    yoyChartSeries() {
      if (!this.yoyData || !this.yoyData.results[this.yoySelectedStudio]) {
        return []
      }
      // Hier müssen wir aufpassen: Das Array muss transformed werden
      const studioData = this.yoyData.results[this.yoySelectedStudio]
      return Object.keys(studioData).map((year) => ({
        name: year,
        data: studioData[year].map((val) => this.getEffectiveValue(val)), // NEU: Transformiere Werte
      }))
    },
    subscriptionRevenue() {
      if (!this.data?.revenueBySubscription) return { total: 0 }
      return this.data.revenueBySubscription.reduce(
        (acc, curr) => {
          acc.total += curr.total
          return acc
        },
        { total: 0 }
      )
    },
    mainCategorySelectItems() {
      if (!this.data) return []
      const mainCategories = new Set()
      if (this.data.revenueBySubscription?.length > 0) {
        mainCategories.add("Abonnements")
      }
      if (this.data.revenueByCategory) {
        this.data.revenueByCategory.forEach((cat) => mainCategories.add(cat.main))
      }
      return Array.from(mainCategories)
        .sort()
        .map((name) => ({ name: name }))
    },
    chartTitle() {
      if (this.drilldownLevel === 1) return `Details für: ${this.drilldownHistory[1]}`
      return `Umsatz nach ${this.selectedMainCategory}`
    },
    dynamicChartSeries() {
      if (!this.data) return []
      if (this.drilldownLevel === 1) {
        return this.drilldownItemData
          ? this.drilldownItemData
              .map((item) => ({
                x: item.name,
                y: this.getEffectiveValue(item.total), // NEU: Netto
                count: item.count,
              }))
              .sort((a, b) => b.y - a.y)
          : []
      }

      let dataToShow =
        this.selectedMainCategory === "Abonnements"
          ? this.data.revenueBySubscription
          : this.data.revenueByCategory.filter((cat) => cat.main === this.selectedMainCategory)

      return (dataToShow || [])
        .map((item) => ({
          x: item.sub || item.name,
          y: this.getEffectiveValue(item.total), // NEU: Netto
          count: item.count,
        }))
        .sort((a, b) => b.y - a.y)
    },
    dynamicChartOptions() {
      return {
        chart: { type: "bar", foreColor: "#9CA3AF", events: { dataPointSelection: this.handleChartClick }, toolbar: { show: false } },
        xaxis: { categories: this.dynamicChartSeries.map((item) => item.x) },
        yaxis: { labels: { formatter: (val) => this.formatCurrency(val) } },
        tooltip: {
          theme: "dark",
          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            const count = w.config.series[seriesIndex].data[dataPointIndex].count
            const revenue = series[seriesIndex][dataPointIndex]
            const categoryName = w.globals.labels[dataPointIndex]

            return `<div class="pa-2">
                            <div><strong>${categoryName}</strong></div>
                            <div>Umsatz: <strong>${this.formatCurrency(revenue)}</strong></div>
                            <div>Verkäufe: <strong>${count} Stk.</strong></div>
                        </div>`
          },
        },
        plotOptions: { bar: { borderRadius: 4, horizontal: false, columnWidth: "60%" } },
        dataLabels: { enabled: false },
        grid: { borderColor: "#374151", strokeDashArray: 4 },
        colors: [this.chartColors[0]],
      }
    },
    revenueOverTimeSeries() {
      if (!this.data?.revenueOverTime) return []
      return [
        {
          name: "Umsatz",
          data: this.data.revenueOverTime.map((d) => [
            new Date(d.date).getTime(),
            this.getEffectiveValue(d.total), // NEU: Netto
          ]),
        },
      ]
    },
    revenueOverTimeOptions() {
      return {
        chart: { type: "area", foreColor: "#9CA3AF", height: 350, zoom: { enabled: false }, toolbar: { show: false } },
        title: { text: "Umsatzentwicklung im Zeitraum", align: "left", style: { fontSize: "18px", color: "#F9FAFB" } },
        colors: [this.chartColors[1]],
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2 },
        fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1, stops: [0, 90, 100] } },
        xaxis: { type: "datetime", labels: { datetimeUTC: false, format: "dd. MMM" } },
        yaxis: { labels: { formatter: (val) => this.formatCurrency(val) } },
        tooltip: { theme: "dark", x: { format: "dd. MMMM yyyy" }, y: { formatter: (val) => this.formatCurrency(val) } },
        grid: { borderColor: "#374151", strokeDashArray: 4 },
      }
    },
    mainCategorySeries() {
      if (!this.data) return { series: [], labels: [] }
      const categories = {}
      this.data.revenueByCategory.forEach((item) => {
        if (!categories[item.main]) categories[item.main] = 0
        categories[item.main] += this.getEffectiveValue(item.total) // NEU: Netto
      })
      if (this.subscriptionRevenue.total > 0) {
        categories["Abonnements"] = this.getEffectiveValue(this.subscriptionRevenue.total) // NEU: Netto
      }
      return { series: Object.values(categories), labels: Object.keys(categories) }
    },
    mainCategoryOptions() {
      return {
        chart: { type: "donut", foreColor: "#ccc" },
        title: { text: "Umsatz nach Hauptkategorie", align: "left", style: { fontSize: "18px", color: "#F9FAFB" } },
        labels: this.mainCategorySeries.labels,
        colors: this.chartColors,
        tooltip: { theme: "dark", y: { formatter: (val) => this.formatCurrency(val) } },
        legend: { position: "bottom" },
        plotOptions: {
          pie: {
            donut: {
              labels: {
                show: true,
                total: { show: true, label: "Gesamt", formatter: (w) => this.formatCurrency(w.globals.seriesTotals.reduce((a, b) => a + b, 0)) },
              },
            },
          },
        },
      }
    },
    genderSeries() {
      if (!this.data?.revenueByGender) return { series: [], labels: [] }
      const { male, female, other } = this.data.revenueByGender
      // NEU: Netto
      return {
        series: [this.getEffectiveValue(male.total), this.getEffectiveValue(female.total), this.getEffectiveValue(other.total)],
        labels: ["Männlich", "Weiblich", "Andere"],
      }
    },
    genderOptions() {
      return {
        chart: { type: "pie", foreColor: "#ccc" },
        title: { text: "Umsatz nach Geschlecht", align: "left", style: { fontSize: "18px", color: "#F9FAFB" } },
        colors: [this.chartColors[4], this.chartColors[5], this.chartColors[6]],
        labels: this.genderSeries.labels,
        tooltip: { theme: "dark", y: { formatter: (val) => this.formatCurrency(val), title: { formatter: (s) => s } } },
        legend: { position: "bottom" },
      }
    },
    filteredDetailedTransactions() {
      let items = this.detailedTransactions
      if (this.filters.customer) {
        const s = this.filters.customer.toLowerCase()
        items = items.filter((i) => `${i.FIRSTNAME || ""} ${i.LASTNAME || ""}`.toLowerCase().includes(s))
      }
      if (this.filters.contractName) {
        const s = this.filters.contractName.toLowerCase()
        items = items.filter((i) => i.ContractName && i.ContractName.toLowerCase().includes(s))
      }
      if (this.filters.description) {
        const s = this.filters.description.toLowerCase()
        items = items.filter((i) => i.DESCRIPTION && i.DESCRIPTION.toLowerCase().includes(s))
      }
      if (this.filters.accountName) {
        const s = this.filters.accountName.toLowerCase()
        items = items.filter((i) => i.AccountName && i.AccountName.toLowerCase().includes(s))
      }
      if (this.filters.hasAdjustment) {
        items = items.filter((item) => item.adjustment)
      }

      return items
    },
    filteredTotalValue() {
      return this.filteredDetailedTransactions.reduce((sum, item) => {
        const valueToAdd = item.netValue !== null ? item.netValue : item.VALUE
        return sum + Math.abs(this.getEffectiveValue(valueToAdd)) // NEU: Netto
      }, 0)
    },
    uniqueContractNames() {
      const names = this.detailedTransactions.map((t) => t.ContractName).filter(Boolean)
      return [...new Set(names)].sort()
    },
    uniqueAccountNames() {
      const names = this.detailedTransactions.map((t) => t.AccountName).filter(Boolean)
      return [...new Set(names)].sort()
    },
    quarterlyChartOptions() {
      return {
        ...this.yoyChartOptions,
        xaxis: { categories: ["Q1", "Q2", "Q3", "Q4"] },
        stroke: { show: true, width: 2, colors: ["transparent"] },
        dataLabels: { enabled: false },
        tooltip: {
          theme: "dark",
          shared: true,
          intersect: false,
          y: {
            formatter: (val, { seriesIndex, dataPointIndex, w }) => {
              const currentYearName = w.globals.seriesNames[seriesIndex]

              const chronologicalYears = [...this.yoyComparisonData.years].reverse()
              const currentYearData = chronologicalYears.find((y) => y.year == currentYearName)
              const previousYearData = chronologicalYears.find((y) => y.year == currentYearName - 1)

              if (!currentYearData) return this.formatCurrency(val)

              const previousQuarterValue = previousYearData ? previousYearData.quarterlyData[dataPointIndex] : 0

              if (previousQuarterValue > 0) {
                const growth = ((val - previousQuarterValue) / previousQuarterValue) * 100
                const sign = growth > 0 ? "+" : ""
                const color = growth > 0 ? "#10B981" : "#EF4444"
                return `${this.formatCurrency(val)} <span style="font-weight: 600; color: ${color};">(${sign}${growth.toFixed(1)}%)</span>`
              }

              return this.formatCurrency(val)
            },
          },
        },
      }
    },
    yoyComparisonData() {
      if (!this.aggregatedStudioDataForYoY) {
        return { years: [], kpis: {} }
      }

      const studioData = this.aggregatedStudioDataForYoY
      const years = [...this.yoyData.years].sort((a, b) => a - b)
      const today = new Date()
      const currentYear = today.getFullYear()
      const currentMonthIndex = today.getMonth()
      const currentDay = today.getDate()
      let allTimeBestMonth = { year: 0, month: "", revenue: 0 }
      let bestGrowthMonth = { year: 0, month: "", growth: -Infinity }

      const processedYears = []

      years.forEach((year) => {
        // NEU: WICHTIG! Wir wandeln die Rohdaten HIER um, damit alle Berechnungen (Kumuliert, Q1-Q4) Netto sind
        const yearData = studioData[year].map((val) => this.getEffectiveValue(val))

        // Auch die Vorjahresdaten müssen transformiert werden für den Vergleich
        const rawPreviousYearData = studioData[year - 1]
        const previousYearData = rawPreviousYearData ? rawPreviousYearData.map((val) => this.getEffectiveValue(val)) : null

        const monthlyDetails = yearData.map((revenue, i) => {
          if (revenue > allTimeBestMonth.revenue) {
            allTimeBestMonth = { year, month: i, revenue }
          }

          const prevMonthRevenue = previousYearData ? previousYearData[i] : 0

          let growth = 0
          if (prevMonthRevenue && prevMonthRevenue > 0) {
            growth = ((revenue - prevMonthRevenue) / prevMonthRevenue) * 100
            if (growth > bestGrowthMonth.growth) {
              bestGrowthMonth = { year, month: i, growth }
            }
          }
          return { revenue, growth }
        })

        processedYears.push({
          year,
          total: yearData.reduce((sum, r) => sum + r, 0),
          monthlyDetails,
          cumulativeData: yearData.reduce((acc, val) => [...acc, (acc.length > 0 ? acc[acc.length - 1] : 0) + val], []),
          quarterlyData: [0, 1, 2, 3].map((q) => yearData.slice(q * 3, q * 3 + 3).reduce((sum, r) => sum + r, 0)),
        })
      })

      const thisYear = processedYears.find((p) => p.year === currentYear)
      const lastYear = processedYears.find((p) => p.year === currentYear - 1)
      let ytdGrowth = 0,
        ytdCurrent = 0,
        ytdPrevious = 0
      if (thisYear && lastYear) {
        const daysInCurrentMonth = new Date(currentYear, currentMonthIndex + 1, 0).getDate()
        ytdCurrent =
          thisYear.monthlyDetails.slice(0, currentMonthIndex).reduce((sum, m) => sum + m.revenue, 0) +
          (thisYear.monthlyDetails[currentMonthIndex].revenue / daysInCurrentMonth) * (currentDay - 1)
        const daysInPreviousMonth = new Date(currentYear - 1, currentMonthIndex + 1, 0).getDate()
        ytdPrevious =
          lastYear.monthlyDetails.slice(0, currentMonthIndex).reduce((sum, m) => sum + m.revenue, 0) +
          (lastYear.monthlyDetails[currentMonthIndex].revenue / daysInPreviousMonth) * (currentDay - 1)
        if (ytdPrevious > 0) {
          ytdGrowth = ((ytdCurrent - ytdPrevious) / ytdPrevious) * 100
        }
      }
      const months = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"]

      return {
        years: processedYears.reverse(),
        kpis: {
          ytdCurrent,
          ytdPrevious,
          ytdGrowth,
          bestMonth: `${months[allTimeBestMonth.month]} ${allTimeBestMonth.year}`,
          bestMonthRevenue: allTimeBestMonth.revenue,
          bestGrowth: `${months[bestGrowthMonth.month]} ${bestGrowthMonth.year}`,
          bestGrowthValue: bestGrowthMonth.growth,
        },
      }
    },
    mainYoYChartSeries() {
      if (!this.yoyComparisonData.years || this.yoyComparisonData.years.length === 0) return []
      const latestYear = this.yoyComparisonData.years[0]
      const otherYears = this.yoyComparisonData.years.slice(1)

      let series = [
        {
          name: latestYear.year.toString(),
          type: "bar",
          data: latestYear.monthlyDetails.map((m) => m.revenue),
        },
      ]

      otherYears.forEach((year) => {
        series.push({
          name: year.year.toString(),
          type: "bar",
          data: year.monthlyDetails.map((m) => m.revenue),
        })
      })
      return series.reverse()
    },
    cumulativeChartOptions() {
      return {
        ...this.yoyChartOptions,
        chart: { type: "line", foreColor: "#9CA3AF", toolbar: { show: true } },
        stroke: { width: 3, curve: "smooth" },
        markers: { size: 0 },
        tooltip: {
          theme: "dark",
          shared: true,
          intersect: false,
          y: {
            formatter: (val, { seriesIndex, dataPointIndex, w }) => {
              const currentYearName = w.globals.seriesNames[seriesIndex]

              const chronologicalYears = [...this.yoyComparisonData.years].reverse()
              const currentYearData = chronologicalYears.find((y) => y.year == currentYearName)
              const previousYearData = chronologicalYears.find((y) => y.year == currentYearName - 1)

              if (!currentYearData) return this.formatCurrency(val)

              const previousCumulativeValue = previousYearData ? previousYearData.cumulativeData[dataPointIndex] : 0

              if (previousCumulativeValue > 0) {
                const growth = ((val - previousCumulativeValue) / previousCumulativeValue) * 100
                const sign = growth > 0 ? "+" : ""
                const color = growth > 0 ? "#10B981" : "#EF4444"
                return `${this.formatCurrency(val)} <span style="font-weight: 600; color: ${color};">(${sign}${growth.toFixed(1)}%)</span>`
              }

              return this.formatCurrency(val)
            },
          },
        },
      }
    },
    cumulativeChartSeries() {
      if (!this.yoyComparisonData.years) return []
      return this.yoyComparisonData.years
        .map((year) => ({
          name: year.year.toString(),
          data: year.cumulativeData,
        }))
        .reverse()
    },
    quarterlyChartSeries() {
      if (!this.yoyComparisonData.years) return []
      return this.yoyComparisonData.years
        .map((year) => ({
          name: year.year.toString(),
          data: year.quarterlyData,
        }))
        .reverse()
    },
    yoySplitChartData() {
      if (!this.yoyComparisonData.years || this.yoyComparisonData.years.length < 2) return []

      const charts = []
      const chronologicalYears = [...this.yoyComparisonData.years].reverse()

      for (let i = 1; i < chronologicalYears.length; i++) {
        const currentYearStats = chronologicalYears[i]
        const previousYearStats = chronologicalYears[i - 1]
        charts.unshift({
          title: `${previousYearStats.year} vs. ${currentYearStats.year}`,
          series: [
            { name: previousYearStats.year.toString(), data: previousYearStats.monthlyDetails.map((m) => m.revenue) },
            { name: currentYearStats.year.toString(), data: currentYearStats.monthlyDetails.map((m) => m.revenue) },
          ],
          options: {
            ...this.yoyChartOptions,
            chart: { type: "bar", foreColor: "#9CA3AF", toolbar: { show: true } },
            stroke: { show: true, width: 2, colors: ["transparent"] },
            markers: { size: 0 },
            dataLabels: { enabled: false },
          },
        })
      }
      return charts
    },
    yoyChartOptions() {
      return {
        chart: { type: "bar", foreColor: "#9CA3AF", toolbar: { show: true } },
        stroke: { width: [0, 3, 3, 3, 3, 3, 3], curve: "smooth" },
        markers: { size: 4 },
        xaxis: {
          categories: ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"],
        },
        yaxis: { title: { text: "Umsatz (CHF)" }, labels: { formatter: (val) => this.formatCurrency(val) } },
        tooltip: {
          theme: "dark",
          shared: true,
          intersect: false,
          y: {
            formatter: (val, { seriesIndex, dataPointIndex, w }) => {
              const year = w.globals.seriesNames[seriesIndex]
              const yearStats = this.yoyComparisonData.years.find((d) => d.year == year)
              if (!yearStats) return this.formatCurrency(val)
              const monthStats = yearStats.monthlyDetails[dataPointIndex]
              const growth = monthStats.growth
              const oldestYear = [...this.yoyData.years].sort((a, b) => a - b)[0]
              if (growth !== 0 && yearStats.year > oldestYear) {
                const sign = growth > 0 ? "+" : ""
                const color = growth > 0 ? "#10B981" : "#EF4444"
                return `${this.formatCurrency(val)} <span style="font-weight: 600; color: ${color};">(${sign}${growth.toFixed(1)}%)</span>`
              }
              return this.formatCurrency(val)
            },
          },
        },
        grid: { borderColor: "#374151", strokeDashArray: 4 },
        colors: this.chartColors,
        legend: { position: "top", horizontalAlign: "left" },
      }
    },
  },
  watch: {
    yoySelectedStudios(newSelection, oldSelection) {
      if (!oldSelection) return

      const justAddedGesamt = newSelection.includes("Gesamt") && !oldSelection.includes("Gesamt")
      const justRemovedGesamt = !newSelection.includes("Gesamt") && oldSelection.includes("Gesamt")
      const justAddedStudio = newSelection.length > oldSelection.length && !justAddedGesamt

      if (justAddedGesamt) {
        this.yoySelectedStudios = ["Gesamt"]
        return
      }

      if (justAddedStudio && newSelection.length > 1 && oldSelection.includes("Gesamt")) {
        this.yoySelectedStudios = newSelection.filter((s) => s !== "Gesamt")
        return
      }

      if (newSelection.length === 0) {
        this.yoySelectedStudios = ["Gesamt"]
      }
    },
    yoyData(newData) {
      if (newData && newData.years && newData.years.length >= 2) {
        this.compareYearA = newData.years[0]
        this.compareYearB = newData.years[1]
      }
    },
    compareYearA(newYearA) {
      if (newYearA === this.compareYearB) {
        this.compareYearB = this.availableYearsForComparison.find((y) => y !== newYearA) || null
      }
    },
    yoyYearsBack() {
      if (this.showYoyDialog) this.fetchYoyData()
    },
    selectedDataType() {
      this.drillUp()
      this.fetchTransactionStatistics()
      if (this.showYoyDialog) {
        this.fetchYoyData()
      }
    },
    async datePickerRange() {
      await this.fetchTransactionStatistics()
      await this.fetchInvoicesStatistics()
    },
    async studios() {
      await this.fetchTransactionStatistics()
      await this.fetchInvoicesStatistics()
    },
    showDetailsDialog(isOpening) {
      if (isOpening) {
        this.fetchTransactionDetails()
      } else {
        this.filters = { customer: "", contractName: "", description: "", accountName: "", hasAdjustment: false }
      }
    },
    selectedMainCategory() {
      this.drillUp()
    },
    async selectedDataType() {
      this.drillUp()
      await this.fetchTransactionStatistics()
    },
  },
}
</script>

<style lang="scss" scoped>
.kpi-card {
  background-color: #1f2937;
  border: 1px solid #374151;
  transition: all 0.2s ease-in-out;
}
.kpi-card[hover]:hover {
  transform: translateY(-4px);
  border-color: #4f5a6e;
  cursor: pointer;
}
</style>
